import { FunctionComponent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RoutesList } from '../../../../routes/Routes';
import { usePlanningBreachStore } from '../../../../hooks/usePlanningBreachStore';
import { ErrorMessages } from '@types';
import { parseZodErrors, stringFieldSchema, textAreaInputSchema } from '@utils';
import { SELECT_OPTIONS_ERROR } from '../constants/errors';
import { ZodError } from 'zod';
import { ErrorSummary } from '@atoms/*';
import {
  FormTitle,
  NavigationButtons,
  RadioOptions,
  TextArea,
} from '@molecules/*';

export const DoesOwnerLiveAtProperty: FunctionComponent = () => {
  const navigate = useNavigate();
  const { data, update } = usePlanningBreachStore();
  const [errorMessages, setErrorMessages] = useState<ErrorMessages[]>([]);
  const title = 'Does the owner live at the property?';
  const MAX_LENGTH = 500;
  const propertyDetailsLabelText =
    'Give details of when the owner lives at the property';
  const propertyDetailsDescription =
    'For example, if they only live there at the weekend.';

  const options = [
    {
      id: 'Yes',
      title: 'Yes',
      labelText: 'Yes',
      isDefault: data.doesOwnerLiveAtProperty === 'Yes',
    },
    {
      id: 'No',
      title: 'No',
      labelText: 'No',
      isDefault: data.doesOwnerLiveAtProperty === 'No',
    },
    {
      id: 'DoNotKnow',
      title: 'DoNotKnow',
      labelText: 'I do not know',
      isDefault: data.doesOwnerLiveAtProperty === 'DoNotKnow',
    },
  ];

  const handleChangeOptions = (selection: string) => {
    setErrorMessages([]);
    const updatedSelection = options.filter(
      (type) => type.labelText === selection,
    )[0].id as 'Yes' | 'No' | 'DoNotKnow';

    if (updatedSelection) {
      update('doesOwnerLiveAtProperty', updatedSelection);
    }
    if (updatedSelection !== 'Yes') {
      update('doesOwnerLiveAtPropertyDetails', '');
    }
  };

  const handleChangePropertyDetails = (
    event: React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    update('doesOwnerLiveAtPropertyDetails', event.currentTarget.value);
    setErrorMessages([]);
  };

  const buttonPrevious = {
    onClick: () => {
      navigate(RoutesList.SHORT_TERM_LETS_RESPONSIBILITY);
    },
  };

  const buttonNext = {
    onClick: async () => {
      try {
        stringFieldSchema(
          'ownerLiveAtProperty',
          SELECT_OPTIONS_ERROR.required.message,
        ).parse({
          ownerLiveAtProperty: data.doesOwnerLiveAtProperty,
        });
        if (data.doesOwnerLiveAtProperty === 'Yes') {
          textAreaInputSchema({
            fieldName: 'detailsOfOwner',
            requiredMessage: 'Enter details about the issue.',
            maxLength: MAX_LENGTH,
            maxLengthMessage: `Details must be ${MAX_LENGTH} characters or less.`,
          }).parse({
            detailsOfOwner: data.doesOwnerLiveAtPropertyDetails,
          });
        }
        navigate(RoutesList.SHORT_TERM_LETS_AD);
      } catch (error) {
        const validationErrors = parseZodErrors(error as ZodError);
        setErrorMessages(
          validationErrors.map((error: ErrorMessages) => ({
            ...error,
            path: error.path,
          })),
        );
      }
    },
  };

  return (
    <>
      <div className={' mt-6'}>
        <ErrorSummary messages={errorMessages} />
      </div>
      <FormTitle text={title} />
      <RadioOptions
        options={options}
        onChange={handleChangeOptions}
        errorPath={'doesOwnerLiveAtProperty'}
        errorMessages={errorMessages}
      />
      {data.doesOwnerLiveAtProperty === 'Yes' ? (
        <TextArea
          labelText={propertyDetailsLabelText}
          isLabelTextTitle={false}
          description={propertyDetailsDescription}
          value={data.doesOwnerLiveAtPropertyDetails}
          onChange={handleChangePropertyDetails}
          errorPath={'detailsOfOwner'}
          errorMessages={errorMessages}
        />
      ) : null}
      <NavigationButtons
        buttonNext={buttonNext}
        buttonPrevious={buttonPrevious}
      />
    </>
  );
};
